import React, { useState, useEffect } from 'react';
import UserForm from './UserForm';
import api from '../utils/api';
import ReactPaginate from 'react-paginate';
import './UserManagement.css';
import useAuth from '../context/useAuth';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const { role,resultsPerPage } = useAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/api/users');
        setUsers(response.data);
        setError(null);
      } catch (error) {
        console.error('There was an error fetching the users:', error);
        setError('Failed to fetch users. Please try again.');
      }
    };
    fetchUsers();

    // Focus on search input when component loads
    const searchInput = document.getElementById('search-input');
    if (searchInput) searchInput.focus();
  }, []);

  const handleSave = async (updatedUser) => {
    try {
      const response = await api.put(`/api/users/${updatedUser.id}`, updatedUser);
      setUsers(users.map(user => user.id === updatedUser.id ? response.data : user));
      setSelectedUserId(null);
      setError(null);
    } catch (error) {
      console.error('Failed to update user:', error);
      setError('Failed to update user. Please try again.');
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const handleEdit = (userId) => {
    setSelectedUserId(userId);
  };

  const handleResetPassword = async (userId, userName, userEmail) => {
    if (window.confirm(`Are you sure you want to reset the password for ${userName} and send it to ${userEmail}?`)) {
      try {
        await api.post(`/api/users/${userId}/reset-password`);
        alert(`Password reset successfully. An email has been sent to ${userEmail}.`);
        setError(null);
      } catch (error) {
        console.error('Failed to reset password:', error);
        setError('Failed to reset password. Please try again.');
      }
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pageCount = Math.ceil(filteredUsers.length / resultsPerPage);
  const offset = currentPage * resultsPerPage;
  const currentUsers = filteredUsers.slice(offset, offset + resultsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  return (
    <div>
      <h1>User Management</h1>
      {error && <div style={{ color: 'red' }} className="error-message">{error}</div>}
      <input
        type="text"
        id="search-input"
        placeholder="Search users"
        value={searchQuery}
        onChange={handleSearch}
      />
      <table className="user-table" striped="true" bordered="true" hover="true" size="sm" style={{ fontSize: 12 }} aria-label="User Management Table">
        <thead>
          <tr>
            <th>User Id</th>
            <th>Username</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role</th>
            <th>Member ID</th>
            <th>Active?</th>
            <th>Deleted?</th>
            <th>Must Change Password?</th>
            <th>OTP Required?</th>
            <th>Last Login</th>
            <th>Reset Password</th>
          </tr>
        </thead>
        <tbody>
          {currentUsers.map(user => (
            <tr
              key={user.id}
              className={selectedUserId === user.id ? 'selected' : ''}
              onClick={() => handleEdit(user.id)}
            >
              <td>{user.id}</td>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.phone}</td>
              <td>{user.role}</td>
              <td>{user.memberId}</td>
              <td>{user.isActive ? 'Yes' : 'No'}</td>
              <td>{user.isDeleted ? 'Yes' : 'No'}</td>
              <td>{user.mustChangePassword ? 'Yes' : 'No'}</td>
              <td>{user.isOTPRequired ? 'Yes' : 'No'}</td>
              <td>{user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}</td>
              <td>
                {role === 'admin' || (role === 'ops' && user.role === 'member') ? (
                  <button onClick={() => handleResetPassword(user.id, user.name, user.email)}>
                    Reset Password
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {pageCount > 1 && (
        <>
          <ReactPaginate
            previousLabel={'Previous'}
            nextLabel={'Next'}
            breakLabel={'...'}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={handlePageClick}
            containerClassName={'pagination'}
            activeClassName={'active'}
            previousClassName={currentPage === 0 ? 'disabled' : ''}
            nextClassName={currentPage === pageCount - 1 ? 'disabled' : ''}
          />
          <div className="pagination-summary">
            Page {currentPage + 1} of {pageCount}
          </div>
        </>
      )}
      {selectedUserId && (
        <div className="user-form-container">
          <h2>Edit User</h2>
          <UserForm
            user={users.find(user => user.id === selectedUserId)}
            onSave={handleSave}
            currentUserRole={role}
          />
        </div>
      )}
    </div>
  );
};

export default UserManagement;